import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTheme } from '@/client/theme/theme-provider';
import { money } from '../../HumanResource/components/comp-format';
import type { Report } from '@/client/types/business/spend';

/** Validated categorical slots 1 and 2 (dataviz reference palette), light / dark. */
const SLOTS = [
  { light: '#2a78d6', dark: '#3987e5' },
  { light: '#eb6834', dark: '#d95926' },
];

export const monthShort = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });
export const thisMonth = () => new Date().toISOString().slice(0, 7);
export const shiftMonth = (month: string, by: number) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1 + by, 1)).toISOString().slice(0, 7);
};

/** Two series per month (in vs out), one currency, one axis; legend + hover. */
export function InOutChart({
  points,
  currency,
  labels,
  height = 240,
}: {
  points: { month: string; a: number; b: number; net: number }[];
  currency: string;
  labels: [string, string];
  height?: number;
}) {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const color = (i: number) => (dark ? SLOTS[i].dark : SLOTS[i].light);
  const data = points.map((p) => ({ ...p, label: monthShort(p.month) }));
  return (
    <div>
      <div className="mb-2 flex gap-4">
        {labels.map((l, i) => (
          <span key={l} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color(i) }} />
            {l}
          </span>
        ))}
      </div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barGap={2}>
            <CartesianGrid vertical={false} stroke={dark ? '#2a2a28' : '#f3f4f6'} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: dark ? '#c3c2b7' : '#6b7280' }} axisLine={false} tickLine={false} />
            <YAxis
              tick={{ fontSize: 11, fill: dark ? '#c3c2b7' : '#6b7280' }}
              axisLine={false}
              tickLine={false}
              width={64}
              tickFormatter={(v: number) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v))}
            />
            <Tooltip
              cursor={{ fill: dark ? '#2a2a28' : '#f3f4f6' }}
              formatter={(value: number, key: string) => [money(value, currency), key === 'a' ? labels[0] : labels[1]]}
              labelFormatter={(_, payload) => {
                const p = payload?.[0]?.payload as { month: string; net: number } | undefined;
                return p ? `${monthShort(p.month)} · net ${money(p.net, currency)}` : '';
              }}
            />
            <Bar dataKey="a" fill={color(0)} radius={[4, 4, 0, 0]} maxBarSize={28} />
            <Bar dataKey="b" fill={color(1)} radius={[4, 4, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** Body of a two-sided monthly report: chart, then a table of every line by month. */
export function ReportBody<A extends string, B extends string>({
  report,
  a,
  b,
  labels,
  currency,
  lineLabel = (k: string) => k,
}: {
  report: Report<A, B>;
  a: A;
  b: B;
  labels: [string, string];
  currency: string;
  lineLabel?: (key: string) => string;
}) {
  // Each row carries one {line: amount} map per side, keyed by the side's name.
  type Row = { month: string; net: number; totals: Record<string, number> } & Record<string, unknown>;
  const rows = report.rows.filter((r) => r.currency === currency) as unknown as Row[];
  const byMonth = new Map(rows.map((r) => [r.month, r]));
  const sideOf = (r: Row | undefined, side: string) => (r?.[side] ?? {}) as Record<string, number>;
  const lines = (side: string) => [...new Set(rows.flatMap((r) => Object.keys(sideOf(r, side))))].sort();
  const points = report.months.map((m) => {
    const r = byMonth.get(m);
    return { month: m, a: r?.totals[a] ?? 0, b: r?.totals[b] ?? 0, net: r?.net ?? 0 };
  });
  const cell = (m: string, side: string, key: string) => sideOf(byMonth.get(m), side)[key] ?? 0;
  const sum = (side: string, key?: string) =>
    report.months.reduce((n, m) => n + (key ? cell(m, side, key) : byMonth.get(m)?.totals[side] ?? 0), 0);

  const section = (side: string, title: string) => (
    <>
      <tr className="bg-gray-50">
        <td className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500" colSpan={report.months.length + 2}>
          {title}
        </td>
      </tr>
      {lines(side).map((k) => (
        <tr key={side + k}>
          <td className="px-3 py-1.5 text-gray-700">{lineLabel(k)}</td>
          {report.months.map((m) => (
            <td key={m} className="px-3 py-1.5 text-right text-gray-700">
              {cell(m, side, k) ? money(cell(m, side, k), currency) : '—'}
            </td>
          ))}
          <td className="px-3 py-1.5 text-right text-gray-900">{money(sum(side, k), currency)}</td>
        </tr>
      ))}
      <tr className="font-medium">
        <td className="px-3 py-1.5 text-gray-900">Total {title.toLowerCase()}</td>
        {report.months.map((m) => (
          <td key={m} className="px-3 py-1.5 text-right text-gray-900">
            {money(byMonth.get(m)?.totals[side] ?? 0, currency)}
          </td>
        ))}
        <td className="px-3 py-1.5 text-right text-gray-900">{money(sum(side), currency)}</td>
      </tr>
    </>
  );

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <InOutChart points={points} currency={currency} labels={labels} />
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left font-medium">{currency}</th>
              {report.months.map((m) => (
                <th key={m} className="px-3 py-2 text-right font-medium">
                  {monthShort(m)}
                </th>
              ))}
              <th className="px-3 py-2 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {section(a, labels[0])}
            {section(b, labels[1])}
            <tr className="border-t-2 border-gray-200 font-semibold">
              <td className="px-3 py-2 text-gray-900">Net</td>
              {report.months.map((m) => {
                const n = byMonth.get(m)?.net ?? 0;
                return (
                  <td key={m} className={`px-3 py-2 text-right ${n < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {money(n, currency)}
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right text-gray-900">{money(points.reduce((n, p) => n + p.net, 0), currency)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
